import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Card, CardBody, Col, FormGroup, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import { LuLock, LuUsers, LuZap } from "react-icons/lu";
import { GoShieldCheck } from "react-icons/go";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import "./styles.css";
import countries from "../../../../data/countries";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import DefaultButton from "../../../../components/Buttons/Default";
import { BILLING_PRICES } from "../../../../utils/prices";
import { post } from "../../../../utils/helpers/api";
import { FaWhatsapp } from "react-icons/fa6";

function PaymentForm({ order }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState({
    country: "",
    state: "",
    city: "",
    address: "",
    postal_code: "",
  });

  function set(field) {
    return (e) => setBilling((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Billing address */}
      <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.06em" }}>
        {t('billing.billingAddress')}
      </p>

      <FormGroup>
        <Label className="billing-pay-label">{t('common.country')}</Label>
        <Select
          value={
            countries.map((c) => ({ value: c.slug, label: c.name })).find((o) => o.value === billing.country) || null
          }
          onChange={(sel) => setBilling((prev) => ({ ...prev, country: sel?.value || "" }))}
          options={countries.map((c) => ({ value: c.slug, label: c.name }))}
          styles={SelectCustomStyles}
          placeholder={t('common.selectCountry')}
        />
      </FormGroup>

      <FormGroup>
        <Label className="billing-pay-label">{t('settings.streetAddress')}</Label>
        <Input type="text" placeholder={t('billing.streetAddressPlaceholder')} value={billing.address} onChange={set("address")} required />
      </FormGroup>

      <FormGroup>
        <Label className="billing-pay-label">{t('settings.state')}</Label>
        <Input type="text" placeholder={t('billing.statePlaceholder')} value={billing.state} onChange={set("state")} required />
      </FormGroup>

      <Row className="g-2 mb-3">
        <Col sm={7}>
          <FormGroup className="mb-0">
            <Label className="billing-pay-label">{t('settings.city')}</Label>
            <Input type="text" placeholder={t('settings.city')} value={billing.city} onChange={set("city")} required />
          </FormGroup>
        </Col>
        <Col sm={5}>
          <FormGroup className="mb-0">
            <Label className="billing-pay-label">{t('settings.zipPostal')}</Label>
            <Input type="text" placeholder={t('billing.zipPlaceholder')} value={billing.postal_code} onChange={set("postal_code")} required />
          </FormGroup>
        </Col>
      </Row>

      <hr style={{ borderColor: "rgba(0,0,0,0.07)", margin: "1.5rem 0" }} />

      {/* Card details */}
      <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.06em" }}>
        {t('billing.cardDetails')}
      </p>

      <DefaultButton
        type="submit"
        color="primary"
        block
        className="mt-4"
        loading={loading}
      >
        <LuLock size={14} className="me-2" />
        {t('billing.pay')} {order.symbol}{order.total.toLocaleString()} / {order.cycle === "monthly" ? t('billing.moAbbr') : t('billing.yrAbbr')}
      </DefaultButton>

      <p className="text-center text-muted mt-3 mb-0" style={{ fontSize: "0.75rem" }}>
        <GoShieldCheck size={13} className="me-1" />
        {t('billing.sslPciNote')}
      </p>
    </form>
  );
}

function FormSkeleton() {
  return (
    <div className="billing-skeleton">
      {[80, 100, 100, 56, 100, 120, 48].map((w, i) => (
        <div key={i} className="billing-skeleton-line" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

function PayPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();

  const currency = state?.currency || "usd";
  const billingCycle = state?.billingCycle || "monthly";
  const userCount = state?.userCount || 2;
  const prices = BILLING_PRICES[currency];

  const order = {
    plan: "Nazar Pro",
    users: userCount,
    cycle: billingCycle,
    symbol: prices.symbol,
    pricePerUser: prices.monthly,
    total: userCount * prices[billingCycle],
  };

  const [clientSecret, setClientSecret] = useState(null);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          title={t('billing.completePayment')}
          onBack={() => navigate("/settings/billing/subscribe")}
        />

        <div className="subheader-page-content scrollable">
          <Row className="g-4 align-items-start">

            {/* Payment form */}
            <Col lg={7}>
              <Card>
                <CardBody style={{ padding: "1.75rem" }}>
                  <Alert color="info" className="mb-3">
                    { t('settings.noBillingOptions') }
                  </Alert>

                  <a href="https://wa.me/213562723905" target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp size={36} color="#075e54" />
                  </a>
                </CardBody>
              </Card>
            </Col>

            {/* Order summary */}
            <Col lg={5}>
              <Card>
                <CardBody style={{ padding: "1.75rem" }}>
                  <p className="text-muted small fw-semibold text-uppercase mb-3" style={{ letterSpacing: "0.06em" }}>
                    {t('billing.orderSummary')}
                  </p>

                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div className="billing-pay-plan-icon">
                      <LuZap size={18} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#1d1d1f" }}>
                        {order.plan}
                      </div>
                      <div style={{ fontSize: "0.8125rem", color: "#6e6e73" }}>
                        {t('billing.allModulesIncluded')}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2 mb-3">
                    <div className="billing-pay-summary-row">
                      <span className="billing-pay-summary-label">
                        <LuUsers size={13} className="me-1" />
                        {t('billing.users')}
                      </span>
                      <span className="billing-pay-summary-value">{order.users} {t('billing.seats')}</span>
                    </div>
                    <div className="billing-pay-summary-row">
                      <span className="billing-pay-summary-label">{t('billing.billingCycle')}</span>
                      <span className="billing-pay-summary-value">
                        {order.cycle === "monthly" ? t('billing.monthly') : t('billing.yearly')}
                      </span>
                    </div>
                    <div className="billing-pay-summary-row">
                      <span className="billing-pay-summary-label">{t('billing.pricePerUser')}</span>
                      <span className="billing-pay-summary-value">
                        {order.symbol}{order.pricePerUser} {t('billing.perUserPerMonth')}
                      </span>
                    </div>
                  </div>

                  <hr style={{ borderColor: "rgba(0,0,0,0.07)", margin: "1rem 0" }} />

                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontWeight: 600, fontSize: "0.9375rem", color: "#1d1d1f" }}>
                      {t('billing.totalDueToday')}
                    </span>
                    <span style={{ fontWeight: 800, fontSize: "1.375rem", color: "#6C63FF", letterSpacing: "-0.5px" }}>
                      {order.symbol}{order.total.toLocaleString()}
                      <span style={{ fontSize: "0.8125rem", fontWeight: 400, color: "#6e6e73" }}>
                        {" "}/ {order.cycle === "monthly" ? t('billing.moAbbr') : t('billing.yrAbbr')}
                      </span>
                    </span>
                  </div>

                  <div className="billing-pay-note mt-4">
                    <p className="mb-0 text-muted" style={{ fontSize: "0.8125rem" }}>
                      {t('billing.youWillBeCharged')}{" "}
                      <strong>
                        {order.symbol}{order.total.toLocaleString()}/{order.cycle === "monthly" ? t('billing.monthWord') : t('billing.yearWord')}
                      </strong>
                      . {t('billing.cancelAnytimeNote')}
                    </p>
                  </div>
                </CardBody>
              </Card>
            </Col>

          </Row>
        </div>
      </div>
    </SidebarLayout>
  );
}

export default PayPage;
